import { Link } from 'react-router-dom';
import PublicHeader from '../components/PublicHeader';
import PublicFooter from '../components/PublicFooter';
import {
  BookOpen, CheckCircle2, BarChart3, ShieldCheck,
  ArrowRight, Sparkles, Smile, Heart, Zap, Repeat, TrendingUp,
} from 'lucide-react';

const pillars = [
  { slug: 'bookkeeping', title: 'Bookkeeping', icon: BookOpen, desc: 'AI categorizes every transaction with firm-specific context.' },
  { slug: 'close', title: 'Close', icon: CheckCircle2, desc: 'Readiness scoring, exception queues, and faster month-ends.' },
  { slug: 'reporting', title: 'Reporting', icon: BarChart3, desc: 'Statements, budgets, and multi-entity insights in seconds.' },
  { slug: 'controls', title: 'Controls', icon: ShieldCheck, desc: 'Credit card tracking, audit trails, missing-doc chasers.' },
];

const integrations = [
  'QuickBooks', 'Xero', 'Stripe', 'Plaid', 'Gmail', 'Slack',
  'Dropbox', 'Ramp', 'Brex', 'Bill.com', 'NetSuite', 'Sage',
];

const satisfactionMetrics = [
  {
    key: 'csat',
    label: 'CSAT',
    fullName: 'Customer Satisfaction Score',
    value: '96%',
    suffix: 'satisfied',
    benchmark: 'Industry average: 78%',
    delta: '+18 pts',
    summary: 'Of firms rate SnuggleMind "satisfied" or "very satisfied" within their first close cycle — the highest band the survey allows.',
    proof: 'Measured post-interaction across 4,200+ verified survey responses, rolling 12 months.',
    icon: 'Smile',
    featured: true,
  },
  {
    key: 'nps',
    label: 'NPS',
    fullName: 'Net Promoter Score',
    value: '+72',
    suffix: 'world-class',
    benchmark: 'SaaS leader band starts at +50',
    delta: 'Top 1% of B2B',
    summary: 'Firms actively recommend SnuggleMind to peers, putting our loyalty score alongside Apple, Costco, and Tesla.',
    proof: 'Calculated quarterly from anonymized renewal and exit surveys.',
    icon: 'Heart',
  },
  {
    key: 'ces',
    label: 'CES',
    fullName: 'Customer Effort Score',
    value: '1.8',
    suffix: 'out of 7',
    benchmark: 'Lower is better · Avg: 3.4',
    delta: '−47% effort',
    summary: 'Onboarding, importing data, and resolving exceptions takes nearly half the effort compared with legacy bookkeeping platforms.',
    proof: 'CES surveyed at three friction points: signup, first import, and first close.',
    icon: 'Zap',
  },
  {
    key: 'retention',
    label: 'Retention',
    fullName: 'User Retention Rate',
    value: '94%',
    suffix: '12-month',
    benchmark: 'SaaS benchmark: 80%',
    delta: '+14 pts',
    summary: 'Of firms who start a SnuggleMind subscription are still active twelve months later — and 81% upgrade their tier.',
    proof: 'Rolling 12-month cohort retention across all paid plans.',
    icon: 'Repeat',
  },
];

const metricIconMap = { Smile, Heart, Zap, Repeat };


export default function LandingPage() {
  return (
    <div className="min-h-screen" style={{ background: '#F7F6F2' }}>
      <PublicHeader />

      {/* HERO */}
      <section className="relative overflow-hidden pt-20 pb-24 lg:pt-28 lg:pb-32">
        <div className="max-w-[1280px] mx-auto px-6 relative z-10">
          <div className="max-w-3xl">
            <p className="text-[11px] uppercase tracking-[0.24em] font-semibold mb-6" style={{ color: '#D98A4A' }} data-testid="hero-eyebrow">
              One Platform. Infinite Advantage.
            </p>
            <h1
              className="font-heading font-semibold tracking-[-0.03em] leading-[0.95] mb-6"
              style={{ fontSize: 'clamp(3rem, 8vw, 7rem)', color: '#0A3622' }}
              data-testid="hero-headline"
            >
              Close the books. <span style={{ color: '#D98A4A' }}>Faster.</span>
            </h1>
            <p className="text-[18px] lg:text-[20px] leading-relaxed max-w-xl mb-8" style={{ color: '#4B5563' }}>
              Everything your firm needs to automate bookkeeping, close month-end, and stay ahead — without the grind.
            </p>

            {/* Hero CTAs — Trial + Watch Tour */}
            <div className="flex flex-wrap items-center gap-3 mb-10" data-testid="hero-cta-row">
              <Link
                to="/register"
                className="inline-flex items-center gap-2 h-12 px-7 rounded-full text-[14px] font-body font-semibold transition-all hover:scale-[1.02]"
                style={{ background: '#D98A4A', color: '#FFFFFF' }}
                data-testid="hero-cta-trial"
              >
                Start Free Trial <ArrowRight size={16} />
              </Link>
              <Link
                to="/tour"
                className="group inline-flex items-center gap-2.5 h-12 pl-3 pr-6 rounded-full text-[14px] font-body font-semibold transition-all"
                style={{ background: 'transparent', color: '#0A3622', border: '1.5px solid #0A3622' }}
                data-testid="hero-cta-tour"
              >
                <span className="w-7 h-7 rounded-full flex items-center justify-center transition-transform group-hover:scale-110" style={{ background: '#0A3622', color: '#FFFFFF' }}>
                  <span className="ml-0.5" style={{ borderLeft: '7px solid #FFFFFF', borderTop: '5px solid transparent', borderBottom: '5px solid transparent', display: 'inline-block' }} />
                </span>
                Watch the 90-second tour
              </Link>
            </div>
          </div>

          {/* 4 Pillars row */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 max-w-5xl" data-testid="hero-pillars">
            {pillars.map((p) => (
              <Link
                key={p.slug}
                to={`/solutions/${p.slug}`}
                className="group relative flex items-center gap-3 p-5 rounded-2xl transition-all duration-200 hover:-translate-y-1"
                style={{ background: '#FFFFFF', border: '1px solid #E5E3DB' }}
                data-testid={`hero-pillar-${p.slug}`}
              >
                <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors" style={{ background: 'rgba(217,138,74,0.1)' }}>
                  <p.icon size={22} style={{ color: '#D98A4A' }} />
                </div>
                <div className="flex-1">
                  <p className="font-heading text-[15px] font-semibold" style={{ color: '#0A3622' }}>{p.title}</p>
                </div>
                <ArrowRight size={16} className="opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: '#D98A4A' }} />
              </Link>
            ))}
          </div>
        </div>

        {/* Decorative bars */}
        <div className="absolute right-0 top-0 bottom-0 w-1/3 pointer-events-none hidden lg:flex items-end justify-end gap-3 pb-20 pr-10 opacity-80">
          {[120, 180, 140, 210, 160].map((h, i) => (
            <div key={`bar-${i}-${h}`} style={{ height: `${h}px`, width: '18px', background: i === 2 ? '#D98A4A' : '#0A3622', opacity: i === 2 ? 1 : 0.1 + i * 0.15, borderRadius: '3px' }} />
          ))}
        </div>
      </section>

      {/* LOGO SCROLLER intentionally removed — no client company name listings. */}

      {/* CALLOUT */}
      <section className="py-24 text-center">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="font-heading font-semibold tracking-tight mb-6" style={{ fontSize: 'clamp(2rem, 4vw, 3.25rem)', color: '#0A3622', lineHeight: 1.1 }}>
            Leading firms aren't waiting for the future. <em className="not-italic" style={{ color: '#D98A4A' }}>They're building it with SnuggleMind.</em>
          </h2>
          <p className="text-[17px] leading-relaxed mb-8" style={{ color: '#4B5563' }}>
            We're partnering with forward-looking firms to deliver trusted technology that moves the profession forward.
          </p>
          <Link
            to="/about"
            className="inline-flex items-center gap-2 h-11 px-7 rounded-full text-[14px] font-body font-semibold transition-colors"
            style={{ background: 'transparent', color: '#0A3622', border: '1.5px solid #0A3622' }}
            data-testid="callout-cta"
          >
            Get to know us <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* MODULAR SOLUTIONS */}
      <section className="pb-24" data-testid="solutions-grid">
        <div className="max-w-[1280px] mx-auto px-6">
          <div className="text-center mb-16">
            <h3 className="font-heading font-semibold tracking-tight max-w-3xl mx-auto" style={{ fontSize: 'clamp(1.5rem, 3vw, 2.25rem)', color: '#0A3622', lineHeight: 1.25 }}>
              SnuggleMind's modular solutions deliver value on their own, but together they consolidate your workflow and introduce the firm-driven innovation the profession's been waiting for.
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { slug: 'bookkeeping', title: 'Automate bookkeeping', cta: 'Accelerate payments', icon: BookOpen, color: '#0A3622', bg: 'rgba(10,54,34,0.06)' },
              { slug: 'close', title: 'Close with confidence', cta: 'Modernize close', icon: CheckCircle2, color: '#D98A4A', bg: 'rgba(217,138,74,0.1)' },
              { slug: 'reporting', title: 'Report in real time', cta: 'Elevate insights', icon: BarChart3, color: '#0A3622', bg: 'rgba(10,54,34,0.06)' },
              { slug: 'controls', title: 'Lock down controls', cta: 'Revolutionize controls', icon: ShieldCheck, color: '#D98A4A', bg: 'rgba(217,138,74,0.1)' },
            ].map((s) => (
              <Link
                key={s.slug}
                to={`/solutions/${s.slug}`}
                className="group p-7 rounded-3xl transition-all duration-300 hover:-translate-y-1.5 flex flex-col"
                style={{ background: '#FFFFFF', border: '1px solid #E5E3DB', minHeight: '340px' }}
                data-testid={`solution-card-${s.slug}`}
              >
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6" style={{ background: s.bg }}>
                  <s.icon size={28} style={{ color: s.color }} />
                </div>
                <h4 className="font-heading text-[22px] font-semibold mb-4" style={{ color: '#0A3622', lineHeight: 1.15 }}>{s.title}</h4>
                <div className="mt-auto pt-4">
                  <span className="inline-flex items-center gap-1.5 text-[13px] font-body font-semibold" style={{ color: '#D98A4A' }}>
                    {s.cta} <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* INTEGRATIONS RING */}
      <section className="py-24 relative overflow-hidden" style={{ background: '#111815' }} data-testid="integrations-section">
        <div className="max-w-[1280px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-[11px] uppercase tracking-[0.24em] font-semibold mb-4" style={{ color: '#D98A4A' }}>Integrations & Technology Partners</p>
            <h2 className="font-heading font-semibold tracking-tight text-white mb-6" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', lineHeight: 1.1 }}>
              Your connected ecosystem
            </h2>
            <p className="text-[17px] leading-relaxed mb-8" style={{ color: 'rgba(247,246,242,0.7)' }}>
              Unlock the freedom to integrate with the tools you use today and the flexibility to scale for tomorrow by connecting with leading accounting technology.
            </p>
            <Link
              to="/connect"
              className="inline-flex items-center gap-2 h-11 px-7 rounded-full text-[14px] font-body font-semibold transition-colors"
              style={{ background: '#D98A4A', color: '#FFFFFF' }}
              data-testid="integrations-cta"
            >
              Get connected <ArrowRight size={16} />
            </Link>
          </div>

          <div className="relative h-[440px] flex items-center justify-center">
            {/* Orbit rings */}
            {[180, 280, 380].map((size) => (
              <div key={size} className="absolute rounded-full" style={{ width: `${size}px`, height: `${size}px`, border: '1px solid rgba(217,138,74,0.18)' }} />
            ))}
            {/* Center node */}
            <div className="absolute w-20 h-20 rounded-full flex items-center justify-center z-10" style={{ background: '#D98A4A' }}>
              <span className="font-heading text-[28px] font-bold text-white">S</span>
            </div>
            {/* Floating integration chips */}
            {integrations.slice(0, 7).map((name, i) => {
              const angle = (i / 7) * Math.PI * 2;
              const r = 170;
              const x = Math.cos(angle) * r;
              const y = Math.sin(angle) * r;
              return (
                <div
                  key={name}
                  className="absolute px-4 py-2 rounded-full font-body text-[12.5px] font-medium animate-float"
                  style={{
                    transform: `translate(${x}px, ${y}px)`,
                    background: 'rgba(247,246,242,0.08)',
                    border: '1px solid rgba(217,138,74,0.25)',
                    color: '#F7F6F2',
                    animationDelay: `${i * 0.3}s`,
                  }}
                >
                  {name}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SATISFACTION METRICS */}
      <SatisfactionMetrics />

      {/* ALLIANCES SCROLLER intentionally removed — no client/alliance company name listings. */}

      {/* BIG CTA */}
      <section className="relative overflow-hidden py-28" style={{ background: '#0A3622' }} data-testid="final-cta">
        <div className="max-w-[1280px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center relative z-10">
          <div>
            <h2 className="font-heading font-semibold tracking-tight text-white mb-6" style={{ fontSize: 'clamp(2.25rem, 4.5vw, 3.5rem)', lineHeight: 1.05 }}>
              Putting trust into technology for firms and their clients.
            </h2>
            <p className="text-[17px] leading-relaxed mb-8 max-w-xl" style={{ color: 'rgba(247,246,242,0.75)' }}>
              Join leading firms who trust SnuggleMind to drive innovation, consolidate systems, and shape the future of the profession.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/register"
                className="inline-flex items-center gap-2 h-12 px-8 rounded-full text-[15px] font-body font-semibold transition-all hover:scale-[1.02]"
                style={{ background: '#D98A4A', color: '#FFFFFF' }}
                data-testid="final-cta-trial"
              >
                Start Free Trial <ArrowRight size={18} />
              </Link>
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 h-12 px-8 rounded-full text-[15px] font-body font-semibold transition-all"
                style={{ background: 'transparent', color: '#F7F6F2', border: '1.5px solid rgba(247,246,242,0.35)' }}
                data-testid="final-cta-pricing"
              >
                View pricing
              </Link>
            </div>
          </div>
          <div className="hidden lg:flex justify-end pr-10">
            <span className="font-heading font-black leading-none" style={{ fontSize: '16rem', color: '#D98A4A', opacity: 0.15 }}>S</span>
          </div>
        </div>
      </section>

      <PublicFooter />

      {/* Announcement bar */}
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-30 px-4" data-testid="announcement-bar">
        <div className="flex items-center gap-3 px-5 py-3 rounded-full backdrop-blur" style={{ background: 'rgba(17,24,21,0.9)', boxShadow: '0 10px 30px -10px rgba(0,0,0,0.3)' }}>
          <Sparkles size={15} style={{ color: '#D98A4A' }} />
          <p className="text-[13px] font-body" style={{ color: '#F7F6F2' }}>
            Exciting news: SnuggleMind AI is now live with GPT-5.2 reasoning.
            <Link to="/resources" className="ml-2 underline underline-offset-2" style={{ color: '#D98A4A' }}>Learn more</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

function SatisfactionMetrics() {
  return (
    <section
      className="relative overflow-hidden py-24 lg:py-28"
      style={{ background: '#F7F6F2' }}
      data-testid="satisfaction-metrics-section"
    >
      {/* Soft gradient blob */}
      <div
        aria-hidden
        className="absolute -top-32 -right-24 w-[480px] h-[480px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(closest-side, rgba(217,138,74,0.18), transparent 70%)' }}
      />
      <div
        aria-hidden
        className="absolute -bottom-40 -left-20 w-[520px] h-[520px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(closest-side, rgba(10,54,34,0.12), transparent 70%)' }}
      />

      <div className="max-w-[1280px] mx-auto px-6 relative z-10">
        {/* Section heading */}
        <div className="max-w-3xl mb-14">
          <p
            className="text-[11px] uppercase tracking-[0.24em] font-semibold mb-5 inline-flex items-center gap-2"
            style={{ color: '#D98A4A' }}
            data-testid="metrics-eyebrow"
          >
            <TrendingUp size={13} /> Proof, not promises
          </p>
          <h2
            className="font-heading font-semibold tracking-tight"
            style={{ fontSize: 'clamp(2rem, 4vw, 3.25rem)', color: '#0A3622', lineHeight: 1.05 }}
            data-testid="metrics-heading"
          >
            The four numbers modern firms measure us on.
          </h2>
          <p className="text-[16px] mt-5 max-w-2xl leading-relaxed" style={{ color: '#4B5563' }}>
            Customer satisfaction isn't a vibe — it's measurable. Here's how SnuggleMind performs against the
            benchmarks that matter most to firm owners, controllers, and the partners who sign the renewal.
          </p>
        </div>

        {/* Metrics grid */}
        <div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5"
          data-testid="metrics-grid"
        >
          {satisfactionMetrics.map((m) => {
            const Icon = metricIconMap[m.icon];
            const isFeatured = !!m.featured;
            return (
              <article
                key={m.key}
                className="group relative p-7 rounded-2xl flex flex-col transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl"
                style={{
                  background: isFeatured ? '#0A3622' : '#FFFFFF',
                  border: isFeatured ? '1px solid #0A3622' : '1px solid #E5E3DB',
                  color: isFeatured ? '#F7F6F2' : '#1A1A1A',
                  minHeight: '380px',
                }}
                data-testid={`metric-card-${m.key}`}
              >
                {/* Icon + label */}
                <div className="flex items-center justify-between mb-6">
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center"
                    style={{
                      background: isFeatured ? 'rgba(217,138,74,0.18)' : 'rgba(10,54,34,0.06)',
                      color: isFeatured ? '#D98A4A' : '#0A3622',
                    }}
                  >
                    {Icon ? <Icon size={20} strokeWidth={1.8} /> : null}
                  </div>
                  <span
                    className="text-[10px] uppercase tracking-[0.22em] font-semibold"
                    style={{ color: isFeatured ? 'rgba(247,246,242,0.55)' : '#9CA3AF' }}
                    data-testid={`metric-label-${m.key}`}
                  >
                    {m.label}
                  </span>
                </div>

                {/* Full name */}
                <p
                  className="text-[12.5px] font-body font-medium mb-2"
                  style={{ color: isFeatured ? 'rgba(247,246,242,0.7)' : '#4B5563' }}
                >
                  {m.fullName}
                </p>

                {/* Headline value */}
                <div className="flex items-baseline gap-2 mb-3">
                  <span
                    className="font-heading font-semibold tracking-tight"
                    style={{
                      fontSize: 'clamp(2.25rem, 3.6vw, 3rem)',
                      color: isFeatured ? '#F7F6F2' : '#0A3622',
                      lineHeight: 1,
                    }}
                    data-testid={`metric-value-${m.key}`}
                  >
                    {m.value}
                  </span>
                  <span
                    className="text-[12px] font-body font-medium"
                    style={{ color: isFeatured ? 'rgba(247,246,242,0.6)' : '#9CA3AF' }}
                  >
                    {m.suffix}
                  </span>
                </div>

                {/* Delta pill + benchmark */}
                <div className="flex items-center gap-2 mb-5 flex-wrap">
                  <span
                    className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-mono font-semibold"
                    style={{
                      background: isFeatured ? 'rgba(217,138,74,0.22)' : 'rgba(45,122,93,0.12)',
                      color: isFeatured ? '#D98A4A' : '#2D7A5D',
                    }}
                    data-testid={`metric-delta-${m.key}`}
                  >
                    {m.delta}
                  </span>
                  <span
                    className="text-[11.5px] font-body"
                    style={{ color: isFeatured ? 'rgba(247,246,242,0.6)' : '#6B7280' }}
                  >
                    {m.benchmark}
                  </span>
                </div>

                {/* Summary */}
                <p
                  className="text-[13.5px] leading-relaxed mb-5 flex-1"
                  style={{ color: isFeatured ? 'rgba(247,246,242,0.85)' : '#374151' }}
                >
                  {m.summary}
                </p>

                {/* Proof / methodology footer */}
                <div
                  className="pt-4 mt-auto"
                  style={{ borderTop: isFeatured ? '1px solid rgba(247,246,242,0.12)' : '1px solid #E5E3DB' }}
                >
                  <p
                    className="text-[11px] leading-snug"
                    style={{ color: isFeatured ? 'rgba(247,246,242,0.5)' : '#9CA3AF' }}
                  >
                    {m.proof}
                  </p>
                </div>
              </article>
            );
          })}
        </div>

        {/* Footer strip */}
        <div
          className="mt-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-5 rounded-2xl"
          style={{ background: '#FFFFFF', border: '1px solid #E5E3DB' }}
          data-testid="metrics-footer-strip"
        >
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: 'rgba(217,138,74,0.15)', color: '#D98A4A' }}
            >
              <Sparkles size={16} />
            </div>
            <p className="text-[13.5px] font-body leading-snug" style={{ color: '#374151' }}>
              <span className="font-semibold" style={{ color: '#0A3622' }}>Updated quarterly.</span>{' '}
              Aggregated from 4,200+ verified firm surveys and live product telemetry.
            </p>
          </div>
          <Link
            to="/register"
            className="inline-flex items-center gap-2 h-10 px-5 rounded-full text-[13px] font-body font-semibold transition-all hover:scale-[1.02] flex-shrink-0"
            style={{ background: '#0A3622', color: '#F7F6F2' }}
            data-testid="metrics-cta-trial"
          >
            Join the firms behind these numbers <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </section>
  );
}

